/**
 * @file poly-selection-handler.ts
 * @description Handles freehand lasso and polygonal selection logic, including vertex dragging and path simplification.
 */

import { g, Tool } from '@hcie/core';
import { 
    buildLassoSelection, 
    buildPolygonalSelection, 
    isPointInSelection, 
    simplifyPath,
    moveSelectionContent 
} from '@hcie/tools';
import { SelectionMode } from '@hcie/shared';

/**
 * @interface Point
 * @description Represents a 2D coordinate on the canvas.
 */
interface Point {
    x: number;
    y: number;
}

export class PolySelectionHandler {
    /**
     * @function handleMouseDown
     * @description Initiates a lasso trace or adds a vertex to a polygonal selection.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if the event was handled.
     */
    public static handleMouseDown(event: MouseEvent): boolean {
        // [CRITICAL_LOGIC_START]
        const currentToolId: string = g.current_tool.id;
        if (currentToolId !== Tool.Lasso.id && currentToolId !== Tool.PolySelect.id) {
            return false;
        }

        // STEP 1: UI Cleanup - Backup current selection mask to a buffer canvas.
        if (g.selectionCanvas) {
            const backupCanvas: HTMLCanvasElement = document.createElement('canvas');
            backupCanvas.width = g.image_width;
            backupCanvas.height = g.image_height;
            const backupCtx = backupCanvas.getContext('2d');
            if (backupCtx) {
                backupCtx.drawImage(g.selectionCanvas as HTMLCanvasElement, 0, 0);
            }
            (g as any)._selectionBaseCanvas = backupCanvas;
        }

        // STEP 2: Vertex Drag Detection
        const handleTolerance: number = 10 / (g.zoom || 1);
        const activePoints: Point[] = (currentToolId === Tool.PolySelect.id) 
            ? ((window as any).polyPoints || []) 
            : ((window as any).lassoPoints || []);
        
        const hoveredVertexIndex: number = this.findHoveredVertexIndex(activePoints, handleTolerance);

        if (hoveredVertexIndex !== -1) {
            (g as any)._draggingPointIndex = hoveredVertexIndex;
            (g as any)._draggingPointSource = currentToolId === Tool.PolySelect.id ? 'poly' : 'lasso';
            g.drawing = true;
            return true;
        }

        // STEP 3: Content Movement
        if (g.isSelectionActive && typeof isPointInSelection === 'function' && isPointInSelection(g.pX, g.pY)) {
            g.movingSelection = true;
            g.startX = g.pX;
            g.startY = g.pY;
            return true;
        }

        // STEP 4: Path Creation
        this.initiatePathCreation(currentToolId);
        
        return true;
        // [CRITICAL_LOGIC_END]
    }

    /**
     * @function handleMouseMove
     * @description Updates the path or moves the selection content while dragging.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if handled.
     */
    public static handleMouseMove(event: MouseEvent): boolean {
        const currentToolId: string = g.current_tool.id;
        if (currentToolId !== Tool.Lasso.id && currentToolId !== Tool.PolySelect.id) return false;
        if (!g.drawing && !g.movingSelection) return false;

        const selectionMode: SelectionMode = ((g as any)._lastSelectionMode || 
            (event.shiftKey ? "add" : (event.altKey ? "subtract" : "replace"))) as SelectionMode;

        // [CRITICAL_LOGIC_START]
        // CASE A: Moving content
        if (g.movingSelection) {
            const deltaX: number = g.pX - g.startX;
            const deltaY: number = g.pY - g.startY;
            
            if (typeof moveSelectionContent === 'function') {
                moveSelectionContent(deltaX, deltaY);
                this.offsetStoredPoints(currentToolId, deltaX, deltaY);
            }
            g.startX = g.pX;
            g.startY = g.pY;
            return true;
        }

        // CASE B: Dragging an existing vertex
        const dragIndex: number = (g as any)._draggingPointIndex;
        if (dragIndex !== -1) {
            const points: Point[] = (g as any)._draggingPointSource === 'poly' 
                ? (window as any).polyPoints 
                : (window as any).lassoPoints;
            
            if (points && points[dragIndex]) {
                points[dragIndex].x = g.pX;
                points[dragIndex].y = g.pY;
                this.rebuildSelection(currentToolId, points, selectionMode);
            }
            return true;
        }

        // CASE C: Tracing Freehand Lasso
        if (currentToolId === Tool.Lasso.id && g.drawing) {
            (window as any).lassoPoints.push({ x: g.pX, y: g.pY });
            buildLassoSelection((window as any).lassoPoints, selectionMode);
            return true;
        }
        // [CRITICAL_LOGIC_END]

        return false;
    }

    /**
     * @function handleMouseUp
     * @description Terminates the current drag operation.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if handled.
     */
    public static handleMouseUp(event: MouseEvent): boolean {
        const currentToolId: string = g.current_tool.id;
        if (currentToolId !== Tool.Lasso.id && currentToolId !== Tool.PolySelect.id) return false;
        if (!g.drawing && !g.movingSelection) return false;

        const selectionMode = ((g as any)._lastSelectionMode || 
            (event.shiftKey ? "add" : (event.altKey ? "subtract" : "replace"))) as SelectionMode;

        // [CRITICAL_LOGIC_START]
        if (g.movingSelection) {
            g.movingSelection = false;
        } else if ((g as any)._draggingPointIndex !== -1) {
            (g as any)._draggingPointIndex = -1;
            g.drawing = false;
        } else if (currentToolId === Tool.Lasso.id) {
            this.finalizeLassoTrace(selectionMode);
        }
        
        // Ensure status bar appears immediately if ghost mode is active
        if ((g as any).settings?.selection?.ghostMode && g.isSelectionActive) {
            const drawingInfo = document.getElementById('drawingInfo');
            if (drawingInfo) drawingInfo.style.display = 'flex';
        }
        
        // [CRITICAL_LOGIC_END]
        
        return true;
    }

    /**
     * @function handleDoubleClick
     * @description Closes the polygonal selection path.
     * @param {MouseEvent} event - The native mouse event.
     * @returns {boolean} - Returns true if handled.
     */
    public static handleDoubleClick(event: MouseEvent): boolean {
        const currentToolId: string = g.current_tool.id;
        if (currentToolId !== Tool.PolySelect.id) return false;
        
        const points: Point[] = (window as any).polyPoints;
        if (!points || points.length < 3) return false;

        // [CRITICAL_LOGIC_START]
        // Remove redundant vertices added by the double-click event.
        this.pruneRedundantPoints(points);

        const selectionMode = ((g as any)._lastSelectionMode || 
            (event.shiftKey ? "add" : (event.altKey ? "subtract" : "replace"))) as SelectionMode;
        
        buildPolygonalSelection(points, selectionMode);
        
        g.isSelectionActive = true;
        if (!(g as any).settings?.selection?.ghostMode) {
            (window as any).polyPoints = [];
            g.drawing = false;
        } else {
            (g as any)._lastSelectionMode = selectionMode;
        }

        // Ensure status bar appears immediately
        const drawingInfo = document.getElementById('drawingInfo');
        if (drawingInfo) drawingInfo.style.display = 'flex';

        // [CRITICAL_LOGIC_END]
        
        return true;
    }

    /**
     * @function getHoverCursor
     * @description Determines if the mouse is over a vertex to show a pointer cursor.
     * @returns {string | null}
     */
    public static getHoverCursor(): string | null {
        const currentToolId: string = g.current_tool.id;
        const points: Point[] = (currentToolId === Tool.PolySelect.id) 
            ? ((window as any).polyPoints || []) 
            : ((window as any).lassoPoints || []);
        
        const tolerance: number = 10 / (g.zoom || 1);
        if (this.findHoveredVertexIndex(points, tolerance) !== -1) {
            return 'pointer';
        }

        if (g.isSelectionActive && typeof isPointInSelection === 'function' && isPointInSelection(g.pX, g.pY)) {
            return 'move';
        }

        return null;
    }

    // --- Private Helper Methods ---

    /**
     * @function findHoveredVertexIndex
     * @private
     */
    private static findHoveredVertexIndex(points: Point[], tolerance: number): number {
        for (let i = 0; i < points.length; i++) {
            if (Math.abs(g.pX - points[i].x) < tolerance && Math.abs(g.pY - points[i].y) < tolerance) {
                return i;
            }
        }
        return -1;
    }

    /**
     * @function initiatePathCreation
     * @private
     */
    private static initiatePathCreation(toolId: string): void {
        if (toolId === Tool.Lasso.id) {
            (window as any).lassoPoints = [{ x: g.pX, y: g.pY }];
            g.drawing = true;
        } else if (toolId === Tool.PolySelect.id) {
            if (!(window as any).polyPoints) (window as any).polyPoints = [];
            (window as any).polyPoints.push({ x: g.pX, y: g.pY });
            g.drawing = true;
            // Force immediate re-render so new point and tooltip appear right away
            import('@hcie/shared').then(({ EventBus }) => EventBus.emit('RENDER_LAYERS'));
        }
    }

    /**
     * @function offsetStoredPoints
     * @private
     */
    private static offsetStoredPoints(toolId: string, deltaX: number, deltaY: number): void {
        const points: Point[] = (toolId === Tool.PolySelect.id) 
            ? (window as any).polyPoints 
            : (window as any).lassoPoints;
            
        if (points) {
            points.forEach(point => {
                point.x += deltaX;
                point.y += deltaY;
            });
        }
    }

    /**
     * @function rebuildSelection
     * @private
     */
    private static rebuildSelection(toolId: string, points: Point[], mode: SelectionMode): void {
        if (toolId === Tool.PolySelect.id) {
            buildPolygonalSelection(points, mode);
        } else {
            buildLassoSelection(points, mode);
        }
    }

    /**
     * @function finalizeLassoTrace
     * @private
     */
    private static finalizeLassoTrace(mode: SelectionMode): void {
        let points: Point[] = (window as any).lassoPoints || [];
        const smoothingTolerance: number = (g as any).settings?.selection?.lassoSmoothing || 0;
        
        if (smoothingTolerance > 0.1 && typeof simplifyPath === 'function') {
            points = simplifyPath(points, smoothingTolerance);
        }
        
        buildLassoSelection(points, mode);
        g.isSelectionActive = true;
        
        if ((g as any).settings?.selection?.ghostMode) {
            (window as any).lassoPoints = points;
            (g as any)._lastSelectionMode = mode;
        } else {
            g.drawing = false;
            (window as any).lassoPoints = [];
        }
    }

    /**
     * @function pruneRedundantPoints
     * @private
     */
    private static pruneRedundantPoints(points: Point[]): void {
        while (points.length > 2) {
            const last: Point = points[points.length - 1];
            const prev: Point = points[points.length - 2];
            if (last.x === prev.x && last.y === prev.y) {
                points.pop();
            } else {
                break;
            }
        }
    }
}
